import { FC } from 'react';
import { Link } from 'react-router-dom';
import { useAppSelector } from '../../store/slice';

// PACKAGES
import { Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';

// UTILS
import SvgMask from '../_Helpers/SvgMask';
import { AnalyticsPushHelpButtonClick } from '../_Helpers/AnalyticsEvents';

// TYPES
import { UserType } from '../../store/slice/User';

export type TitleSectionType = {
	backToText?: string;
	backToUrl?: string;
	title?: string;
	smallTitle?: string;
	smallSubTitle?: string;
	secondaryTitle?: string;
	dynamicTitle?: any;
	description?: string;
	dynamicDescription?: any;
	primaryButtonText?: string;
	primaryButtonHighlight?: boolean;
	primaryButtonClickId?: string;
	secondaryButtonHighlight?: boolean;
	secondaryButtonClickId?: string;
	primaryButtonUrl?: string;
	primaryButtonIcon?: string;
	primaryButtonNotificationIcon?: boolean;
	primaryButtonDisabled?: boolean;
	primaryButtonLoading?: boolean;
	primaryButtonClass?: string;
	primaryButtonClick?: () => void;
	secondaryButtonClick?: () => void;
	secondaryButtonClass?: string;
	secondaryButtonDisabled?: boolean;
	secondaryButtonLoading?: boolean;
	secondaryButtonText?: string;
	secondaryButtonUrl?: string;
	secondaryButtonIcon?: string;
	secondaryButtonNotificationIcon?: boolean;
	helpVideoId?: string;
	hideTitleBar?: boolean;
	isHelpSection?: boolean;
	onHelpClick?: () => void;
	helpText?: string;
	children?: any;
};

const TitleSection: FC<TitleSectionType> = ({
	backToText,
	backToUrl,
	title,
	smallTitle,
	smallSubTitle,
	secondaryTitle,
	dynamicTitle,
	description,
	dynamicDescription,
	primaryButtonText,
	primaryButtonUrl,
	primaryButtonClickId,
	primaryButtonHighlight,
	secondaryButtonClickId,
	secondaryButtonHighlight,
	primaryButtonIcon,
	primaryButtonNotificationIcon,
	primaryButtonDisabled = false,
	primaryButtonLoading = false,
	primaryButtonClass = '',
	primaryButtonClick,
	secondaryButtonClick,
	secondaryButtonClass,
	secondaryButtonDisabled,
	secondaryButtonLoading,
	secondaryButtonText,
	secondaryButtonUrl,
	secondaryButtonIcon,
	secondaryButtonNotificationIcon,
	helpVideoId,
	hideTitleBar,
	isHelpSection = false,
	onHelpClick,
	helpText = 'Help',
	children
}) => {
	const user: UserType = useAppSelector((state) => state.user);

	// const dispatch = useAppDispatch();
	// const helpDismissed = useAppSelector((state) => state.user.helpDismissed);

	// const handleHelpDismiss = useCallback(async () => {
	// 	try {
	// 		const response = await apibridge.postHelpDismiss();
	// 		if (response && response.data && !response.data.isError) {
	// 			dispatch(UserSlice.actions.setHelpDismissed(true));
	// 		}
	// 	} catch (error) {
	// 		console.error('Error dismissing help:', error);
	// 	}
	// }, [dispatch]);

	const trackHelpButtonClick = () => {
		AnalyticsPushHelpButtonClick({
			user,
			pageUrl: window.location.href,
			pagePath: window.location.pathname,
			pageTitle: document.title,
		});
		onHelpClick?.();
	};

	const helpButton = (
		<OverlayTrigger placement="top" overlay={<Tooltip id="b-1">Need help? Click here to view a tutorial</Tooltip>}>
			<button type="button" onClick={trackHelpButtonClick} className="btn-help-section btn">
				<SvgMask path="/svg/Help-Outline.svg" width={20} height={20} />
				<span>{helpText}</span>
			</button>
		</OverlayTrigger>
	);

	return (
		// This must not be wrapped in any elements, or else the position: sticky; on the 'title-section-sub' section will no longer work
		<>
			{!hideTitleBar && (
				<div
					className={`title-section title-section-main ${title || dynamicTitle ? 'has-title' : ''} ${
						children ? 'has-children' : ''
					}`}
				>
					<div className="container">
						<div className="row">
							<div className="col col-lg">
								<div className="left-title-group">
									{backToText && backToUrl && (
										<Link to={backToUrl} className="btn btn-link">
											<SvgMask path="/svg/arrow-left.svg" width={16} height={16} />
											{backToText}
										</Link>
									)}
									{title && (
										<div className="d-flex justify-content-start align-items-center gap-4">
											<h1 className="text-shades-800 title m-0">
												<strong>{title}</strong>
											</h1>

											{helpVideoId ? (
												<OverlayTrigger placement="right" overlay={<Tooltip id="b-1">Video guide</Tooltip>}>
													<Link to="/help-video" className="help-guide-link">
														<SvgMask path="/svg/info-solid.svg" className="guide-icon" width={36} height={36} />
													</Link>
												</OverlayTrigger>
											) : null}
										</div>
									)}

									{smallTitle && (
										<h2 className="text-shades-800 title m-0">
											<strong>{smallTitle}</strong>

											{smallSubTitle && <span className="text-dark-text fw-semibold"> {smallSubTitle}</span>}

											{secondaryTitle && <span className="h2 text-shades-500 title m-0"> / {secondaryTitle}</span>}
										</h2>
									)}
									{dynamicTitle && dynamicTitle}
									{description && <p className="description">{description}</p>}
									{dynamicDescription && dynamicDescription}
								</div>
							</div>
							{primaryButtonClick ||
							primaryButtonText ||
							primaryButtonUrl ||
							secondaryButtonText ||
							secondaryButtonUrl ||
							isHelpSection ? (
								<div className="col-auto col-md-4 col-lg-3">
									<div className="right-button-group">
										{isHelpSection && helpButton}
										{primaryButtonText && primaryButtonUrl && !primaryButtonClick && (
											<Link to={primaryButtonUrl} className={`btn ${primaryButtonClass}`}>
												{primaryButtonIcon && <SvgMask path={primaryButtonIcon} width={16} height={16} />}
												{primaryButtonText}
												{primaryButtonNotificationIcon && <span className="notification-dot flex-shrink-0"></span>}
											</Link>
										)}
										{primaryButtonClick && (
											<button
												id={primaryButtonClickId}
												onClick={primaryButtonClick}
												className={`btn text-nowrap ${primaryButtonClass} ${
													primaryButtonHighlight ? 'highlight-outline' : ''
												}`}
												disabled={primaryButtonDisabled}
											>
												{primaryButtonIcon && <SvgMask path={primaryButtonIcon} width={16} height={16} />}
												{primaryButtonText}
												{primaryButtonNotificationIcon && <span className="notification-dot flex-shrink-0"></span>}
												{primaryButtonLoading && <Spinner animation="border" size="sm" />}
											</button>
										)}

										{secondaryButtonText && secondaryButtonUrl && (
											<Link
												id={secondaryButtonClickId}
												to={secondaryButtonUrl}
												className={`btn btn-white text-nowrap ${secondaryButtonHighlight ? 'highlight-outline' : ''}`}
											>
												{secondaryButtonIcon && <SvgMask path={secondaryButtonIcon} width={16} height={16} />}
												{secondaryButtonText}
												{secondaryButtonNotificationIcon && <span className="notification-dot flex-shrink-0"></span>}
											</Link>
										)}

										{secondaryButtonClick && (
											<button
												id={secondaryButtonClickId}
												onClick={secondaryButtonClick}
												className={`btn btn-white text-nowrap ${secondaryButtonClass} ${
													secondaryButtonHighlight ? 'highlight-outline' : ''
												}`}
												disabled={secondaryButtonDisabled}
											>
												{secondaryButtonIcon && <SvgMask path={secondaryButtonIcon} width={16} height={16} />}
												{secondaryButtonText}
												{secondaryButtonNotificationIcon && <span className="notification-dot flex-shrink-0"></span>}
												{secondaryButtonLoading && <Spinner animation="border" size="sm" />}
											</button>
										)}
									</div>
								</div>
							) : null}
						</div>
					</div>
				</div>
			)}
			{children && (
				<div className={`title-section title-section-sub ${hideTitleBar ? 'title-bar-hidden' : ''}`}>
					<div className="container position-sticky top-0">{children}</div>
				</div>
			)}
		</>
	);
};

export default TitleSection;
